import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import Select from './Select'
import { Target, Plus, CheckCircle2, TrendingUp, MessageSquare, FileText } from 'lucide-react'
import { getCarePlanOutcomes, createOutcome, getOutcomeAnalytics } from '../services/carePlanOutcomes'
import { formatDate } from '../utils/helpers'

const OUTCOME_TYPES = [
  { value: 'goal_achieved', label: 'Goal Achieved' },
  { value: 'symptom_improvement', label: 'Symptom Improvement' },
  { value: 'metric_change', label: 'Metric Change' },
  { value: 'client_feedback', label: 'Client Feedback' },
  { value: 'practitioner_assessment', label: 'Practitioner Assessment' },
]

const CarePlanOutcomes = ({ carePlanId, clientId }) => {
  const [outcomes, setOutcomes] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    outcomeType: 'goal_achieved',
    description: '',
    measurementValue: '',
    measurementUnit: '',
    baselineValue: '',
    recordedDate: new Date().toISOString().slice(0, 10),
  })
  const [submitting, setSubmitting] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (carePlanId && organizationId) {
      fetchOutcomes()
    }
  }, [carePlanId, organizationId])

  const fetchOutcomes = async () => {
    if (!carePlanId || !organizationId) return
    try {
      setLoading(true)
      const [outcomesData, analyticsData] = await Promise.all([
        getCarePlanOutcomes(carePlanId, organizationId),
        getOutcomeAnalytics(carePlanId, organizationId),
      ])
      setOutcomes(outcomesData)
      setAnalytics(analyticsData)
    } catch (error) {
      console.error('Error fetching outcomes:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!carePlanId || !clientId || !organizationId) return

    try {
      setSubmitting(true)
      await createOutcome({
        organizationId,
        carePlanId,
        clientId,
        outcomeType: formData.outcomeType,
        description: formData.description,
        measurementValue: formData.measurementValue || null,
        measurementUnit: formData.measurementUnit || null,
        baselineValue: formData.baselineValue || null,
        recordedDate: formData.recordedDate,
      })

      setFormData({
        outcomeType: 'goal_achieved',
        description: '',
        measurementValue: '',
        measurementUnit: '',
        baselineValue: '',
        recordedDate: new Date().toISOString().slice(0, 10),
      })
      setShowForm(false)
      fetchOutcomes()
    } catch (error) {
      console.error('Error creating outcome:', error)
      alert(error.message || 'Failed to save outcome')
    } finally {
      setSubmitting(false)
    }
  }

  const getOutcomeIcon = (type) => {
    switch (type) {
      case 'goal_achieved':
        return CheckCircle2
      case 'symptom_improvement':
        return TrendingUp
      case 'metric_change':
        return Target
      case 'client_feedback':
        return MessageSquare
      case 'practitioner_assessment':
        return FileText
      default:
        return Target
    }
  }

  const getOutcomeColor = (type) => {
    switch (type) {
      case 'goal_achieved':
        return 'bg-green-100 text-green-700'
      case 'symptom_improvement':
        return 'bg-blue-100 text-blue-700'
      case 'metric_change':
        return 'bg-purple-100 text-purple-700'
      case 'client_feedback':
        return 'bg-yellow-100 text-yellow-700'
      case 'practitioner_assessment':
        return 'bg-gray-100 text-gray-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading outcomes...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Target size={20} />
          Outcome Measurements
        </h3>
        {canWrite('care_plans') && (
          <Button variant="secondary" size="sm" onClick={() => setShowForm(true)}>
            <Plus size={16} className="mr-1" />
            Record Outcome
          </Button>
        )}
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card variant="soft" className="p-4">
            <p className="text-sm text-gray-600">Total Outcomes</p>
            <p className="text-2xl font-bold text-gray-900">{analytics.totalOutcomes}</p>
          </Card>
          <Card variant="soft" className="p-4">
            <p className="text-sm text-gray-600">Goals Achieved</p>
            <p className="text-2xl font-bold text-green-600">{analytics.goalAchieved}</p>
          </Card>
          <Card variant="soft" className="p-4">
            <p className="text-sm text-gray-600">Achievement Rate</p>
            <p className="text-2xl font-bold text-primary-600">{analytics.achievementRate}%</p>
          </Card>
          <Card variant="soft" className="p-4">
            <p className="text-sm text-gray-600">Symptom Improvements</p>
            <p className="text-2xl font-bold text-blue-600">
              {analytics.outcomesByType.symptom_improvement}
            </p>
          </Card>
        </div>
      )}

      {/* Outcome Form */}
      {showForm && canWrite('care_plans') && (
        <Card variant="soft" className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Record Outcome</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Select
              label="Outcome Type"
              options={OUTCOME_TYPES}
              value={formData.outcomeType}
              onChange={(e) => setFormData({ ...formData, outcomeType: e.target.value })}
              required
            />
            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the outcome..."
              required
              rows={4}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Measurement Value"
                value={formData.measurementValue}
                onChange={(e) => setFormData({ ...formData, measurementValue: e.target.value })}
                placeholder="e.g. 8, 15 lbs, 30%"
              />
              <Input
                label="Unit"
                value={formData.measurementUnit}
                onChange={(e) => setFormData({ ...formData, measurementUnit: e.target.value })}
                placeholder="e.g. kg, points, %"
              />
              <Input
                label="Baseline Value"
                value={formData.baselineValue}
                onChange={(e) => setFormData({ ...formData, baselineValue: e.target.value })}
                placeholder="Starting value"
              />
            </div>
            <Input
              type="date"
              label="Recorded Date"
              value={formData.recordedDate}
              onChange={(e) => setFormData({ ...formData, recordedDate: e.target.value })}
              required
            />
            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Saving...' : 'Save Outcome'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Outcomes List */}
      {outcomes.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <Target size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No outcomes recorded yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {outcomes.map((outcome) => {
            const Icon = getOutcomeIcon(outcome.outcome_type)
            return (
              <Card key={outcome.id} variant="soft" className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${getOutcomeColor(outcome.outcome_type)}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getOutcomeColor(
                            outcome.outcome_type
                          )}`}
                        >
                          {OUTCOME_TYPES.find((t) => t.value === outcome.outcome_type)?.label}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(outcome.recorded_date)}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{outcome.outcome_description}</p>
                    {(outcome.measurement_value || outcome.baseline_value) && (
                      <div className="flex items-center gap-4 text-sm">
                        {outcome.baseline_value && (
                          <span className="text-gray-600">
                            Baseline: <strong>{outcome.baseline_value}</strong>
                          </span>
                        )}
                        {outcome.measurement_value && (
                          <span className="text-gray-900">
                            Current: <strong>
                              {outcome.measurement_value}
                              {outcome.measurement_unit ? ` ${outcome.measurement_unit}` : ''}
                            </strong>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CarePlanOutcomes

